import { HOUSE_H, HOUSE_W, tid } from "@/assets/tileAtlas";
import { TILE } from "@/config";
import type { I18nKey } from "@/i18n";
import { createRng, type LocalizedText, type Rng } from "@/math";

export interface Spawn {
  x: number;
  y: number;
}

export interface MonsterSpawn extends Spawn {
  monsterId: string;
}

export interface SignSpawn extends Spawn {
  /** Text key; "sign.bridge" is resolved by the world to the broken / fixed variant. */
  textKey: I18nKey | "sign.bridge";
}

/** A treasure chest with a monster next to it; the fight is optional. */
export interface GuardedChest {
  id: string;
  x: number;
  y: number;
  itemId: string;
  count: number;
  guard: string;
}

export interface BridgeCell {
  x: number;
  y: number;
  fixedTile: number;
}

/** A named area of the map (tile rectangle); the world announces it when the hero walks in. */
export interface Region {
  id: string;
  name: LocalizedText;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export type TreeKind =
  | "a"
  | "b"
  | "c"
  | "big_a"
  | "big_b"
  | "dead"
  | "round"
  | "autumn"
  | "pink"
  | "palm_a"
  | "palm_b"
  | "palm_c"
  | "rock_tall"
  | "rock_huge"
  | "rock_wide"
  | "rock_tall_grey"
  | "rock_huge_grey"
  | "rock_wide_grey";

/** Trees and rocks are sprites (not tiles) so they can overlap and be depth-sorted with the hero. x,y = bottom centre in pixels. */
export interface TreeSpawn {
  x: number;
  y: number;
  kind: TreeKind;
}

export interface TreeDef {
  w: number;
  h: number;
  /** Texture holding the frame `tree_<kind>`. */
  texture: "tileset-nature" | "tileset-desert";
  /** Solid = the whole footprint collides (rocks); otherwise only the trunk does. */
  solid: boolean;
}

const nat = (w: number, h: number, solid = false): TreeDef => ({ w, h, texture: "tileset-nature", solid });
const des = (w: number, h: number): TreeDef => ({ w, h, texture: "tileset-desert", solid: false });

export const TREE_SIZE: Record<TreeKind, TreeDef> = {
  a: nat(32, 32),
  b: nat(32, 32),
  c: nat(32, 32),
  big_a: nat(64, 48),
  big_b: nat(64, 48),
  dead: nat(64, 48),
  round: nat(48, 48),
  autumn: nat(48, 48),
  pink: nat(48, 48),
  palm_a: des(32, 48),
  palm_b: des(32, 48),
  palm_c: des(32, 48),
  rock_tall: nat(32, 48, true),
  rock_huge: nat(64, 64, true),
  rock_wide: nat(48, 48, true),
  rock_tall_grey: nat(32, 48, true),
  rock_huge_grey: nat(64, 64, true),
  rock_wide_grey: nat(48, 48, true),
};

export type Grid = number[][];

export interface GeneratedMap {
  width: number;
  height: number;
  /** Grass everywhere (never -1). */
  base: Grid;
  /** Dirt, dark grass, sand, water and bridges, autotiled; -1 = nothing. Water collides. */
  ground: Grid;
  /** Flowers, mushrooms, bones — no collision. */
  decor: Grid;
  /** Houses, hedges, rocks, props — everything collides. */
  objects: Grid;
  trees: TreeSpawn[];
  /** Middle cells of the village bridge: water while broken, planks once repaired. */
  bridge: BridgeCell[];
  regions: Region[];
  spawns: {
    player: Spawn;
    npcs: Array<Spawn & { npcId: string }>;
    monsters: MonsterSpawn[];
    chest: Spawn;
    signs: SignSpawn[];
    guardedChests: GuardedChest[];
  };
}

const W = 112;
const H = 80;
const EMPTY = -1;

const center = (tx: number, ty: number): Spawn => ({ x: tx * TILE + TILE / 2, y: ty * TILE + TILE / 2 });

function grid(fill: number): Grid {
  return Array.from({ length: H }, () => Array.from({ length: W }, () => fill));
}

function inBounds(x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x < W && y < H;
}

/**
 * Replace a plain terrain marker with edge / outer-corner / inner-corner variants based on the
 * 8 neighbours. Uses the 3×3 block + 2×2 inner corners of the Ninja Adventure tilesets.
 */
function autotile(g: Grid, kind: string, withInnerCorners: boolean): void {
  const plain = tid(kind);
  const is = (x: number, y: number) => inBounds(x, y) && (g[y] as number[])[x] === plain;
  const out = g.map((row) => [...row]);
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      if (!is(x, y)) continue;
      const n = is(x, y - 1);
      const s = is(x, y + 1);
      const w = is(x - 1, y);
      const e = is(x + 1, y);
      const v = !n ? "t" : !s ? "b" : "";
      const h = !w ? "l" : !e ? "r" : "";
      let id = plain;
      if (v + h !== "") {
        id = tid(`${kind}_${v}${h}`);
      } else if (withInnerCorners) {
        const missing = [!is(x - 1, y - 1), !is(x + 1, y - 1), !is(x - 1, y + 1), !is(x + 1, y + 1)];
        const count = missing.filter(Boolean).length;
        if (count === 1)
          id = tid(`${kind}_${missing[0] ? "itl" : missing[1] ? "itr" : missing[2] ? "ibl" : "ibr"}`);
      }
      (out[y] as number[])[x] = id;
    }
  }
  for (let y = 0; y < H; y++) g[y] = out[y] as number[];
}

export const REGIONS: Region[] = [
  { id: "village", name: { pl: "Countinghollow", en: "Countinghollow" }, x0: 0, y0: 0, x1: 29, y1: 39 },
  { id: "meadows", name: { pl: "Zielone Łąki", en: "Green Meadows" }, x0: 33, y0: 0, x1: 55, y1: 39 },
  { id: "forest", name: { pl: "Szepczący Las", en: "Whispering Forest" }, x0: 56, y0: 0, x1: 111, y1: 41 },
  {
    id: "ruins",
    name: { pl: "Ruiny Starej Rachuby", en: "Ruins of Old Reckoning" },
    x0: 0,
    y0: 40,
    x1: 29,
    y1: 61,
  },
  { id: "mine", name: { pl: "Kopalnia Koralików", en: "Bead Mine" }, x0: 0, y0: 62, x1: 29, y1: 79 },
  { id: "coast", name: { pl: "Wybrzeże Konserw", en: "Cannery Coast" }, x0: 33, y0: 42, x1: 111, y1: 79 },
];

/**
 * The whole world, 112×80 tiles in five regions:
 *  - north-west: Countinghollow (village) and, across the quest-gated bridge, the Green Meadows;
 *  - north-east: the Whispering Forest (dense groves, the forester's hut, owls, snakes, bears);
 *  - south-west: the Ruins of Old Reckoning (dead trees, boulders, skeletons) and the Bead Mine;
 *  - south-east: the Cannery Coast (sand, sea, palms, the Tin Legion and its Sergeant).
 * A second, always-open bridge in the south links the ruins with the coast.
 */
export function buildWorldMap(seed = 20260916): GeneratedMap {
  const rng: Rng = createRng(seed);
  const base = grid(tid("grass"));
  const ground = grid(EMPTY);
  const decor = grid(EMPTY);
  const objects = grid(EMPTY);
  const trees: TreeSpawn[] = [];

  const set = (g: Grid, x: number, y: number, v: number) => {
    if (inBounds(x, y)) (g[y] as number[])[x] = v;
  };
  const get = (g: Grid, x: number, y: number) => (inBounds(x, y) ? ((g[y] as number[])[x] as number) : 0);

  // ---- grass variety (same green, different speckles) ----
  const grassVariants = ["grass_b", "grass_c", "grass_d"].map(tid);
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) if (rng.chance(0.2)) set(base, x, y, rng.pick(grassVariants));

  const rect = (g: Grid, x0: number, y0: number, x1: number, y1: number, v: number, onlyEmpty = false) => {
    for (let y = Math.min(y0, y1); y <= Math.max(y0, y1); y++)
      for (let x = Math.min(x0, x1); x <= Math.max(x0, x1); x++)
        if (!onlyEmpty || get(g, x, y) === EMPTY) set(g, x, y, v);
  };
  const blob = (
    g: Grid,
    cx: number,
    cy: number,
    r: number,
    v: number,
    onlyEmpty = false,
    replace?: number,
  ) => {
    for (let y = Math.floor(cy - r - 1); y <= Math.ceil(cy + r + 1); y++) {
      for (let x = Math.floor(cx - r - 1); x <= Math.ceil(cx + r + 1); x++) {
        const d = Math.hypot(x - cx, y - cy) + rng.next() * 0.9;
        if (d > r) continue;
        const cur = get(g, x, y);
        if (onlyEmpty && cur !== EMPTY) continue;
        if (replace !== undefined && cur !== replace) continue;
        set(g, x, y, v);
      }
    }
  };

  // ---- spawns (needed before trees so their surroundings stay clear) ----
  const monsters: MonsterSpawn[] = [
    // village side: reachable before the bridge is fixed
    { ...center(8, 31), monsterId: "blobble" },
    { ...center(18, 32), monsterId: "blobble" },
    { ...center(26, 12), monsterId: "blobble" },
    { ...center(24, 21), monsterId: "blobble" },
    { ...center(4, 34), monsterId: "blobble" },
    { ...center(26, 22), monsterId: "off_by_one_imp" },
    { ...center(27, 28), monsterId: "off_by_one_imp" },
    { ...center(24, 35), monsterId: "off_by_one_imp" },
    // meadows
    { ...center(38, 10), monsterId: "blobble" },
    { ...center(44, 14), monsterId: "blobble" },
    { ...center(40, 22), monsterId: "blobble" },
    { ...center(50, 20), monsterId: "blobble" },
    { ...center(46, 36), monsterId: "blobble" },
    { ...center(52, 12), monsterId: "off_by_one_imp" },
    { ...center(42, 34), monsterId: "off_by_one_imp" },
    { ...center(51, 30), monsterId: "tin_knight" },
    // forest
    { ...center(62, 9), monsterId: "owl" },
    { ...center(72, 27), monsterId: "owl" },
    { ...center(88, 21), monsterId: "owl" },
    { ...center(104, 30), monsterId: "owl" },
    { ...center(60, 31), monsterId: "snake" },
    { ...center(78, 35), monsterId: "snake" },
    { ...center(92, 9), monsterId: "snake" },
    { ...center(106, 16), monsterId: "snake" },
    { ...center(69, 5), monsterId: "sporeling" },
    { ...center(100, 35), monsterId: "sporeling" },
    { ...center(106, 5), monsterId: "bear" },
    { ...center(82, 27), monsterId: "bear" },
    // ruins & mine
    { ...center(6, 49), monsterId: "mole" },
    { ...center(20, 56), monsterId: "mole" },
    { ...center(8, 61), monsterId: "mole" },
    { ...center(24, 66), monsterId: "mole" },
    { ...center(14, 61), monsterId: "lizard" },
    { ...center(22, 46), monsterId: "lizard" },
    { ...center(4, 44), monsterId: "lizard" },
    { ...center(18, 72), monsterId: "bat" },
    { ...center(26, 74), monsterId: "bat" },
    { ...center(6, 74), monsterId: "bat" },
    { ...center(10, 52), monsterId: "skeleton_counter" },
    { ...center(22, 52), monsterId: "skeleton_counter" },
    // coast
    { ...center(48, 66), monsterId: "octopus" },
    { ...center(70, 67), monsterId: "octopus" },
    { ...center(88, 66), monsterId: "octopus" },
    { ...center(104, 60), monsterId: "octopus" },
    { ...center(56, 63), monsterId: "mollusc" },
    { ...center(84, 59), monsterId: "mollusc" },
    { ...center(100, 66), monsterId: "mollusc" },
    { ...center(40, 61), monsterId: "tin_knight" },
    { ...center(62, 52), monsterId: "tin_knight" },
    { ...center(96, 51), monsterId: "tin_knight" },
    { ...center(46, 46), monsterId: "off_by_one_imp" },
    { ...center(105, 63), monsterId: "sardine_sergeant" },
  ];
  const npcs = [
    { ...center(8, 7), npcId: "abacia" },
    { ...center(8, 16), npcId: "milo" },
    { ...center(20, 15), npcId: "quill" },
    { ...center(15, 12), npcId: "antek" },
    { ...center(87, 14), npcId: "borys" },
    { ...center(18, 47), npcId: "tomik" },
    { ...center(10, 70), npcId: "kazik" },
    { ...center(62, 60), npcId: "marta" },
    { ...center(40, 49), npcId: "mila" },
  ];
  const player = center(13, 16);
  const chest = center(4, 23);
  const guardedChests: GuardedChest[] = [
    { id: "chest_potions", ...center(11, 29), itemId: "big_potion", count: 2, guard: "blobble" },
    { id: "chest_scrolls", ...center(24, 6), itemId: "hint_scroll", count: 3, guard: "sporeling" },
    { id: "chest_vest", ...center(44, 22), itemId: "leather_vest", count: 1, guard: "sporeling" },
    { id: "chest_sword", ...center(45, 11), itemId: "sword_iron", count: 1, guard: "skeleton_counter" },
    { id: "chest_forest", ...center(104, 7), itemId: "hint_scroll", count: 3, guard: "sporeling" },
    { id: "chest_glade", ...center(90, 30), itemId: "big_potion", count: 2, guard: "owl" },
    { id: "chest_ruins", ...center(24, 50), itemId: "big_potion", count: 2, guard: "skeleton_counter" },
    { id: "chest_pickaxe", ...center(12, 66), itemId: "chainmail", count: 1, guard: "lizard" },
    { id: "chest_beach", ...center(52, 66), itemId: "potion", count: 3, guard: "octopus" },
    { id: "chest_coast", ...center(108, 66), itemId: "sword_steel", count: 1, guard: "mollusc" },
  ];

  // ---- roads and plazas ----
  const path = tid("path");
  rect(ground, 2, 18, 97, 19, path); // main road: village → bridge → meadows → forest
  rect(ground, 10, 8, 15, 13, path); // village square
  blob(ground, 12.5, 10.5, 4.2, path); // soften the square into a plaza
  rect(ground, 12, 14, 13, 17, path); // square → main road
  rect(ground, 6, 7, 6, 8, path); // Abacia's door → square
  rect(ground, 6, 8, 9, 8, path);
  rect(ground, 18, 6, 18, 7, path); // house C door → square
  rect(ground, 15, 7, 18, 7, path);
  rect(ground, 6, 15, 6, 17, path); // Milo's door → main road
  rect(ground, 18, 14, 18, 17, path); // training hall door → main road
  rect(ground, 36, 20, 37, 46, path); // meadow path past the pond, south into the coast
  rect(ground, 36, 26, 40, 26, path);
  rect(ground, 11, 20, 12, 58, path); // village → south, between the garden and the farm
  rect(ground, 4, 58, 60, 59, path); // southern road: ruins → second bridge → coast
  rect(ground, 66, 12, 67, 19, path); // forest: to the forester's hut
  rect(ground, 66, 12, 85, 13, path);
  rect(ground, 85, 11, 86, 13, path);
  rect(ground, 96, 19, 97, 46, path); // forest: south to the coast
  rect(ground, 97, 30, 104, 31, path); // forest: side path to a clearing
  blob(ground, 8, 30, 2.6, path, true); // dusty clearing south of the village
  blob(ground, 45, 12, 3.2, path, true); // meadow clearings
  blob(ground, 40, 34, 2.4, path, true);
  blob(ground, 16, 48, 4.4, path, true); // ruins plaza
  blob(ground, 9, 68, 3.6, path, true); // mine yard
  blob(ground, 104, 8, 3.6, path, true); // forest clearings
  blob(ground, 90, 31, 3.2, path, true);
  blob(ground, 74, 30, 2.6, path, true);
  blob(ground, 62, 6, 2.4, path, true);
  blob(ground, 62, 61, 3, path, true); // Marta's yard
  blob(ground, 40, 48, 2.6, path, true); // Mila's post

  // ---- water: the river, the pond, the sea ----
  const waterId = tid("water");
  const seaId = tid("watersand");
  const sandId = tid("sand");
  const SAND_TOP = 62;
  for (let y = 2; y < SAND_TOP; y++)
    for (let x = 30; x <= 32; x++) if (get(ground, x, y) === EMPTY) set(ground, x, y, waterId);
  for (let y = 25; y <= 33; y++) {
    for (let x = 40; x <= 50; x++) {
      const dx = (x - 45) / 4.5;
      const dy = (y - 29) / 3.5;
      if (dx * dx + dy * dy < 0.8 && get(ground, x, y) === EMPTY) set(ground, x, y, waterId);
    }
  }
  // beach and sea along the bottom of the coast; the river's last stretch runs through the sand
  rect(ground, 26, SAND_TOP, 111, 69, sandId, true);
  rect(ground, 26, 70, 111, 79, seaId);
  for (let y = SAND_TOP; y < 70; y++) for (let x = 30; x <= 32; x++) set(ground, x, y, seaId); // river mouth
  for (const [cx, cy, r] of [
    [50, 69.5, 3],
    [80, 68.5, 4],
    [100, 69.5, 3],
    [40, 63, 2.2],
    [70, 62.5, 2.4],
    [108, 63.5, 2.6],
  ] as Array<[number, number, number]>) {
    blob(ground, cx, cy, r, cy > 66 ? seaId : sandId, false, cy > 66 ? sandId : EMPTY);
  }
  blob(ground, 65, 71.5, 2.4, sandId, false, seaId); // sandbanks
  blob(ground, 92, 71.5, 2.8, sandId, false, seaId);
  for (const [cx, cy, r] of [
    [46, 50, 2.2],
    [76, 53, 2.6],
    [58, 47, 1.8],
  ] as Array<[number, number, number]>)
    blob(ground, cx, cy, r, sandId, true);

  // ---- darker grass patches on empty ground ----
  const dark = tid("dark");
  const darkSpots: Array<[number, number, number]> = [
    [4, 34, 3],
    [22, 3, 3.5],
    [24, 28, 3],
    [38, 15, 3.5],
    [48, 22, 3],
    [52, 34, 3],
    [26, 34, 2.5],
    [10, 32, 2],
    // forest: lots of shade
    [60, 26, 4],
    [72, 6, 4.5],
    [80, 22, 5],
    [92, 4, 3.5],
    [104, 20, 4.5],
    [108, 36, 3.5],
    [66, 36, 3.5],
    [86, 38, 3],
    // ruins & mine
    [6, 46, 3],
    [24, 44, 3.2],
    [10, 56, 3.5],
    [22, 60, 3],
    [20, 74, 3.5],
    [4, 74, 2.5],
    // coast scrub
    [50, 56, 3],
    [88, 48, 3.5],
    [104, 50, 3],
    [66, 45, 2.5],
  ];
  for (const [cx, cy, r] of darkSpots) blob(ground, cx, cy, r, dark, true);

  // nothing may spawn in water: turn such cells (and their neighbours) into beach or grass
  const reserved: Spawn[] = [player, chest, ...npcs, ...monsters, ...guardedChests];
  for (const r of reserved) {
    const tx = Math.floor(r.x / TILE);
    const ty = Math.floor(r.y / TILE);
    for (let y = ty - 1; y <= ty + 1; y++)
      for (let x = tx - 1; x <= tx + 1; x++) {
        const v = get(ground, x, y);
        if (v === seaId) set(ground, x, y, sandId);
        else if (v === waterId) set(ground, x, y, EMPTY);
      }
  }

  autotile(ground, "dark", true);
  autotile(ground, "path", true);
  autotile(ground, "sand", true);
  autotile(ground, "water", false);
  autotile(ground, "watersand", false);

  // ---- bridges: road cells over the river become planks ----
  const bridge: BridgeCell[] = [];
  for (let x = 30; x <= 32; x++) {
    set(ground, x, 18, tid("bridge_top"));
    set(ground, x, 19, tid("bridge_bottom"));
    set(ground, x, 58, tid("bridge_top"));
    set(ground, x, 59, tid("bridge_bottom"));
  }
  bridge.push(
    { x: 31, y: 18, fixedTile: tid("bridge_top") },
    { x: 31, y: 19, fixedTile: tid("bridge_bottom") },
  );

  const inRange = (v: number, first: string, last: string) => v >= tid(first) && v <= tid(last);
  const groundFree = (x: number, y: number) => {
    const v = get(ground, x, y);
    return v === EMPTY || inRange(v, "dark", "dark_ibr") || inRange(v, "sand", "sand_ibr");
  };
  const isFree = (x: number, y: number) => inBounds(x, y) && groundFree(x, y) && get(objects, x, y) === EMPTY;
  const isSand = (x: number, y: number) => inRange(get(ground, x, y), "sand", "sand_ibr");

  // ---- houses (4×3 prefabs) ----
  const houses: Array<[string, number, number]> = [
    ["house_a", 5, 4], // Elder Abacia
    ["house_c", 17, 3], // villagers
    ["house_b", 5, 12], // Milo's shop
    ["house_d", 17, 11], // training hall (Wizard Quill)
    ["house_c", 84, 8], // forester's hut
    ["house_b", 58, 57], // Marta's hut
  ];
  for (const [name, x0, y0] of houses)
    for (let r = 0; r < HOUSE_H; r++)
      for (let c = 0; c < HOUSE_W; c++) set(objects, x0 + c, y0 + r, tid(`${name}_${r}_${c}`));

  // ---- props ----
  const put2x2 = (name: string, x: number, y: number) => {
    set(objects, x, y, tid(`${name}_tl`));
    set(objects, x + 1, y, tid(`${name}_tr`));
    set(objects, x, y + 1, tid(`${name}_bl`));
    set(objects, x + 1, y + 1, tid(`${name}_br`));
  };
  put2x2("campfire", 12, 10);
  put2x2("campfire", 16, 48); // the scribe's camp in the ruins
  put2x2("campfire", 62, 63); // Marta's beach fire
  for (const [x, y] of [
    [21, 9],
    [25, 24],
    [9, 34],
    [70, 24],
    [88, 26],
    [64, 32],
    [102, 14],
    [20, 44],
    [6, 52],
    [46, 44],
  ] as Array<[number, number]>)
    put2x2("stump", x, y);
  const signs: SignSpawn[] = [
    { ...center(11, 17), textKey: "sign.junction" },
    { ...center(28, 17), textKey: "sign.bridge" },
    { ...center(13, 12), textKey: "sign.fire" },
    { ...center(13, 40), textKey: "sign.south" },
    { ...center(55, 20), textKey: "sign.forest" },
    { ...center(38, 41), textKey: "sign.coast" },
    { ...center(28, 57), textKey: "sign.bridge2" },
    { ...center(7, 66), textKey: "sign.mine" },
    { ...center(98, 41), textKey: "sign.forestcoast" },
    { ...center(17, 46), textKey: "sign.ruins" },
  ];
  for (const s of signs) set(objects, Math.floor(s.x / TILE), Math.floor(s.y / TILE), tid("signpost"));

  // fenced hay farm: a palisade of logs with a gate on the road side
  const logTop = tid("log_top");
  const logBottom = tid("log_bottom");
  const logLine = (x0: number, y0: number, x1: number, y1: number) => {
    for (let y = Math.min(y0, y1); y <= Math.max(y0, y1); y += 2) {
      for (let x = Math.min(x0, x1); x <= Math.max(x0, x1); x++) {
        set(objects, x, y, logTop);
        set(objects, x, y + 1, logBottom);
      }
    }
  };
  logLine(13, 21, 22, 21); // top
  logLine(13, 27, 22, 27); // bottom
  logLine(13, 23, 13, 25); // left
  logLine(22, 23, 22, 23); // right (gap below = gate)
  logLine(22, 27, 22, 27);
  for (let y = 23; y <= 26; y += 2)
    for (let x = 15; x <= 21; x += 2) set(objects, x, y, rng.chance(0.6) ? tid("hay") : tid("hay_round"));
  // Marta's drying racks: a short palisade by the hut
  logLine(54, 60, 56, 60);
  logLine(54, 62, 54, 62);

  // garden: hedge of bushes with a gate on the road side, flowers inside, a chest
  const bush = tid("bush");
  for (let x = 3; x <= 10; x++) {
    set(objects, x, 22, bush);
    set(objects, x, 27, bush);
  }
  for (let y = 23; y <= 26; y++) {
    set(objects, 3, y, bush);
    set(objects, 10, y, bush);
  }
  set(objects, 10, 24, EMPTY); // gate
  const gardenFlowers = [
    "flower_sun",
    "flower_yellow",
    "flower_red",
    "flower_white",
    "flower_pink",
    "flower_blue",
  ].map(tid);
  for (let y = 23; y <= 26; y++)
    for (let x = 4; x <= 9; x++) if (rng.chance(0.8)) set(decor, x, y, rng.pick(gardenFlowers));

  // boulders (2×2 tiles) and small rocks
  for (const [x, y, grey] of [
    [25, 13, false],
    [36, 16, true],
    [47, 21, false],
    [41, 34, true],
    [52, 8, false],
    [23, 31, true],
    [4, 42, true],
    [26, 48, false],
    [12, 54, true],
    [24, 58, false],
    [14, 74, true],
    [26, 68, true],
    [44, 54, false],
    [72, 47, true],
    [100, 46, false],
    [108, 56, true],
    [60, 24, false],
    [94, 14, true],
  ] as Array<[number, number, boolean]>) {
    if (isFree(x, y) && isFree(x + 1, y) && isFree(x, y + 1) && isFree(x + 1, y + 1))
      put2x2(grey ? "rock_grey" : "rock_big", x, y);
  }

  // cells where nothing may stand (tree trunks, bushes)
  const blocked = grid(0);
  const block = (tx: number, ty: number, radius: number) => {
    for (let y = ty - radius; y <= ty + radius; y++)
      for (let x = tx - radius; x <= tx + radius; x++) set(blocked, x, y, 1);
  };
  for (const m of monsters) block(Math.floor(m.x / TILE), Math.floor(m.y / TILE), 2);
  for (const n of npcs) block(Math.floor(n.x / TILE), Math.floor(n.y / TILE), 2);
  block(Math.floor(player.x / TILE), Math.floor(player.y / TILE), 2);
  block(Math.floor(chest.x / TILE), Math.floor(chest.y / TILE), 1);
  for (const s of signs) block(Math.floor(s.x / TILE), Math.floor(s.y / TILE), 1);
  for (const c of guardedChests) block(Math.floor(c.x / TILE) + 1, Math.floor(c.y / TILE), 2);
  for (const [, hx, hy] of houses)
    for (let y = hy - 1; y <= hy + HOUSE_H; y++)
      for (let x = hx - 1; x <= hx + HOUSE_W; x++) set(blocked, x, y, 1);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) if (!isFree(x, y)) set(blocked, x, y, 1);

  const region = (x: number, y: number): string =>
    REGIONS.find((r) => x >= r.x0 && x <= r.x1 && y >= r.y0 && y <= r.y1)?.id ?? "";

  // ---- scattered bushes, rocks, flowers, mushrooms and bones on free cells ----
  const meadowDecor = [
    "flower_yellow",
    "flower_white",
    "flower_orange",
    "tuft_a",
    "tuft_b",
    "flower_red",
    "flower_blue",
  ].map(tid);
  const forestDecor = ["mushroom_a", "mushroom_b", "mushroom_c", "tuft_a", "tuft_b", "flower_white"].map(tid);
  const ruinsDecor = ["bones_a", "bones_b", "tuft_b", "tuft_a", "rock_small"].map(tid);
  for (let y = 3; y < H - 3; y++) {
    for (let x = 3; x < W - 3; x++) {
      if (get(blocked, x, y)) continue;
      const reg = region(x, y);
      const r = rng.next();
      if (reg === "coast" && isSand(x, y)) {
        if (r < 0.015) {
          set(objects, x, y, tid("rock_small_orange"));
          set(blocked, x, y, 1);
        }
        continue;
      }
      if (r < 0.02) {
        set(objects, x, y, rng.chance(0.7) ? bush : tid("bush_orange"));
        set(blocked, x, y, 1);
      } else if (r < 0.03) {
        set(objects, x, y, rng.chance(0.6) ? tid("rock_small") : tid("rock_small_orange"));
        set(blocked, x, y, 1);
      } else if (r < 0.12) {
        const pool =
          reg === "forest" ? forestDecor : reg === "ruins" || reg === "mine" ? ruinsDecor : meadowDecor;
        const v = rng.pick(pool);
        if (v === tid("rock_small")) {
          set(objects, x, y, v);
          set(blocked, x, y, 1);
        } else set(decor, x, y, v);
      }
    }
  }

  // ---- trees and rocks as sprites ----
  const footprint = (kind: TreeKind) => {
    const d = TREE_SIZE[kind];
    return { halfW: d.solid ? d.w / 2 : d.w >= 64 ? 20 : 12, height: d.solid ? d.h : 14 };
  };
  const fits = (px: number, py: number, kind: TreeKind) => {
    const { halfW, height } = footprint(kind);
    const x0 = Math.floor((px - halfW) / TILE);
    const x1 = Math.floor((px + halfW - 1) / TILE);
    const y0 = Math.floor((py - height) / TILE);
    const y1 = Math.floor((py - 1) / TILE);
    for (let y = y0; y <= y1; y++)
      for (let x = x0; x <= x1; x++) if (!inBounds(x, y) || get(blocked, x, y)) return false;
    return true;
  };
  const addTree = (px: number, py: number, kind: TreeKind, force = false) => {
    if (!force && !fits(px, py, kind)) return false;
    trees.push({ x: px, y: py, kind });
    if (!force) {
      const { halfW, height } = footprint(kind);
      for (let y = Math.floor((py - height) / TILE); y <= Math.floor((py - 1) / TILE); y++)
        for (let x = Math.floor((px - halfW) / TILE); x <= Math.floor((px + halfW - 1) / TILE); x++)
          set(blocked, x, y, 1);
    }
    return true;
  };
  const smallKind = (): TreeKind => rng.pick(["a", "a", "b", "c"]);
  const meadowKind = (): TreeKind => rng.pick(["a", "a", "a", "b", "c", "big_a", "big_b"]);
  const forestKind = (): TreeKind =>
    rng.pick(["a", "b", "c", "big_a", "big_b", "big_a", "round", "autumn", "autumn", "pink"]);
  const ruinsKind = (): TreeKind =>
    rng.pick(["dead", "dead", "dead", "rock_tall", "rock_wide", "rock_huge", "c", "autumn"]);
  const mineKind = (): TreeKind =>
    rng.pick(["rock_tall_grey", "rock_wide_grey", "rock_huge_grey", "rock_tall_grey", "dead"]);
  const coastKind = (): TreeKind => rng.pick(["palm_a", "palm_b", "palm_c", "a", "c"]);

  const wpx = W * TILE;
  const hpx = H * TILE;
  // outer border: two staggered rows of trees on every side
  for (let px = 4; px < wpx + 16; px += 22) {
    addTree(px, 30, smallKind(), true);
    addTree(px + 11, 46, smallKind(), true);
    addTree(px, hpx + 12, smallKind(), true);
    addTree(px + 11, hpx - 4, smallKind(), true);
  }
  for (let py = 40; py < hpx; py += 20) {
    addTree(12, py, smallKind(), true);
    addTree(30, py + 10, smallKind(), true);
    addTree(wpx - 12, py, smallKind(), true);
    addTree(wpx - 30, py + 10, smallKind(), true);
  }
  // region walls: staggered tree rows with gaps where roads pass (tile coordinates)
  const wallH = (ty: number, tx0: number, tx1: number, gaps: Array<[number, number]>) => {
    for (let px = tx0 * TILE + 4; px <= tx1 * TILE + 12; px += 22) {
      const tx = Math.floor(px / TILE);
      if (gaps.some(([g0, g1]) => tx >= g0 && tx <= g1)) continue;
      addTree(px, ty * TILE + 14, smallKind(), true);
      addTree(px + 11, ty * TILE + 30, smallKind(), true);
    }
  };
  const wallV = (tx: number, ty0: number, ty1: number, gaps: Array<[number, number]>) => {
    for (let py = ty0 * TILE + 24; py <= ty1 * TILE + 16; py += 20) {
      const ty = Math.floor(py / TILE);
      if (gaps.some(([g0, g1]) => ty >= g0 && ty <= g1)) continue;
      addTree(tx * TILE + 8, py, smallKind(), true);
      addTree(tx * TILE + 26, py + 10, smallKind(), true);
    }
  };
  wallV(56, 1, 39, [[15, 22]]); // meadows | forest (gap: main road)
  wallH(40, 33, 111, [
    [34, 40],
    [93, 100],
  ]); // north | coast (gaps: meadow path, forest path)
  wallH(40, 0, 27, [[9, 14]]); // village | ruins (gap: south road)

  // groves
  const grove = (cx: number, cy: number, radius: number, count: number, kind: () => TreeKind) => {
    for (let i = 0; i < count * 4; i++) {
      const a = rng.next() * Math.PI * 2;
      const d = Math.sqrt(rng.next()) * radius;
      const px = Math.round((cx + Math.cos(a) * d) / 4) * 4;
      const py = Math.round((cy + Math.sin(a) * d) / 4) * 4;
      if (addTree(px, py, kind())) count--;
      if (count <= 0) break;
    }
  };
  const T = TILE;
  // village & meadows
  grove(44 * T, 6 * T, 140, 26, meadowKind);
  grove(12 * T, 33 * T, 70, 8, meadowKind);
  grove(24 * T, 4 * T, 60, 7, meadowKind);
  grove(36 * T, 30 * T, 50, 5, meadowKind);
  grove(50 * T, 36 * T, 60, 6, meadowKind);
  // forest: dense groves everywhere, the paths stay clear because they are blocked cells
  for (const [cx, cy, r, n] of [
    [64, 6, 90, 14],
    [76, 8, 110, 20],
    [92, 6, 90, 16],
    [62, 26, 110, 18],
    [76, 30, 100, 16],
    [88, 24, 90, 14],
    [104, 24, 100, 18],
    [72, 36, 90, 12],
    [104, 36, 80, 10],
    [100, 14, 90, 14],
  ] as Array<[number, number, number, number]>)
    grove(cx * T, cy * T, r, n, forestKind);
  // ruins: dead trees and boulders; mine: grey rock walls around the yard
  for (const [cx, cy, r, n] of [
    [6, 45, 70, 7],
    [24, 44, 80, 8],
    [10, 56, 80, 8],
    [24, 56, 70, 7],
    [14, 50, 40, 3],
  ] as Array<[number, number, number, number]>)
    grove(cx * T, cy * T, r, n, ruinsKind);
  for (const [cx, cy, r, n] of [
    [6, 64, 60, 6],
    [14, 64, 50, 5],
    [22, 70, 70, 6],
    [8, 76, 60, 5],
  ] as Array<[number, number, number, number]>)
    grove(cx * T, cy * T, r, n, mineKind);
  // coast: palms near the sand, a few trees in the scrub
  for (const [cx, cy, r, n] of [
    [44, 62, 60, 5],
    [58, 64, 50, 4],
    [76, 62, 70, 6],
    [96, 62, 60, 5],
    [108, 60, 40, 3],
    [50, 46, 70, 5],
    [82, 48, 80, 6],
    [104, 48, 60, 4],
  ] as Array<[number, number, number, number]>)
    grove(cx * T, cy * T, r, n, coastKind);
  // lone trees anywhere free (kind by region)
  for (let i = 0; i < 160; i++) {
    const px = rng.int(3 * T, wpx - 3 * T);
    const py = rng.int(4 * T, hpx - 3 * T);
    const reg = region(Math.floor(px / T), Math.floor(py / T));
    const kind =
      reg === "forest"
        ? forestKind()
        : reg === "ruins"
          ? ruinsKind()
          : reg === "mine"
            ? mineKind()
            : reg === "coast"
              ? coastKind()
              : meadowKind();
    addTree(Math.round(px / 4) * 4, Math.round(py / 4) * 4, kind);
  }

  return {
    width: W,
    height: H,
    base,
    ground,
    decor,
    objects,
    trees,
    bridge,
    regions: REGIONS,
    spawns: { player, npcs, monsters, chest, signs, guardedChests },
  };
}

/** Kept for older imports. */
export const buildVillageMap = buildWorldMap;
